/**
 * PIN unlock. Six digits on a custom keypad, and the fastest way back into a shift.
 *
 * Five failed attempts wipes local state and forces a full login — the server revokes the device
 * at the same count, so the two agree. The remaining count is shown from the third attempt, so
 * the wipe is never a surprise.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ErrorCode, isApiError, readHint, signedIn, usePinUnlockMutation, useRememberedShop } from '@pos/shared';

import { useAppDispatch } from '@/app/hooks';
import { Button, InlineError, Keypad, ShopMark, cn } from '@/shared/ui';

const LENGTH = 6;

export function PinUnlock() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hint = readHint();
  // Applies the remembered look and scheme before the PIN lands — same as the sign-in screen.
  const remembered = useRememberedShop();

  const [unlock, { isLoading }] = usePinUnlockMutation();
  const [pin, setPin] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [shake, setShake] = useState(0);

  const submit = async (value: string) => {
    let error: unknown;

    try {
      const session = await unlock({ pin: value }).unwrap();
      dispatch(signedIn(session));
      void navigate('/sell', { replace: true });
      return;
    } catch (caught) {
      error = caught;
    }

    setPin('');
    setShake((count) => count + 1);

    if (isApiError(error) && error.code === ErrorCode.DeviceRevoked) {
      // The wipe and the `revoked` dispatch already happened — `onSessionEnded` in the base
      // query runs synchronously the moment this response is seen, before this catch block
      // does. Doing either again here raced two `wipeLocalState()` calls over the same
      // IndexedDB deletes and could leave this screen reading state RTK Query had already
      // reset out from under it, which is how a clean `DEVICE_REVOKED` response ended up
      // showing "The PIN could not be checked" instead of the explanation screen.
      //
      // The reason is not re-guessed either: this code fires for the PIN limit, a revoke by
      // the owner, and another device signing in, and there is no way to tell them apart from
      // here — claiming "too many wrong PINs" when it was actually someone else signing in
      // would be actively misleading. `onSessionEnded` already set an accurate generic reason.
      void navigate('/device-revoked', { replace: true });
      return;
    }

    setMessage(messageFor(error));
  };

  const press = (digit: string) => {
    if (isLoading || pin.length >= LENGTH) return;

    const next = pin + digit;
    setPin(next);
    setMessage(null);

    // Submits itself at six digits. A confirm button after a fixed-length PIN is a tap that
    // exists only to be tapped.
    if (next.length === LENGTH) void submit(next);
  };

  return (
    // `h-full` with the keypad pushed to the foot: on a phone the digits sit where a thumb
    // already is, not in the middle of the screen. On the wide card there is no extra height,
    // so the same layout simply stacks.
    <div className="flex h-full flex-col items-center gap-8">
      <div className="text-center">
        {/* The remembered shop's mark — the hint carries the shop now. Neutral on a device with
            none, never another shop's. */}
        <div className="mb-4 flex justify-center">
          <ShopMark themeCode={remembered?.themeCode ?? null} neutral={remembered === null} size={52} />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-content-primary">
          {hint?.fullName ?? 'Welcome back'}
        </h1>
        <p className="mt-1 text-sm text-content-muted">Enter your PIN to carry on</p>
      </div>

      <div
        key={shake}
        // `mt-auto` here, on the dots, pushes the dots and the keypad beneath them to the foot of
        // the screen together — the thumb's end of the phone.
        className={cn('mt-auto flex gap-3', shake > 0 && 'motion-safe:animate-[shake_260ms_ease-out]')}
        role="status"
        aria-label={`${pin.length} of ${LENGTH} digits entered`}
      >
        {Array.from({ length: LENGTH }, (_, index) => (
          <span
            key={index}
            className={cn(
              'h-3.5 w-3.5 rounded-full transition-all duration-fast ease-springSubtle',
              // `bg-line` alone is too faint to read as a mark standing on its own — it is a
              // divider colour, never checked for contrast against the page by itself. An empty
              // slot needs a visible outline at rest, not just a low-contrast fill.
              index < pin.length ? 'scale-110 bg-action' : 'border-2 border-line-strong bg-transparent',
            )}
          />
        ))}
      </div>

      {message !== null && <InlineError message={message} />}

      <Keypad
        className="w-full max-w-[19rem]"
        disabled={isLoading}
        onDigit={press}
        onAction={(action) => {
          setMessage(null);
          setPin(action === 'clear' ? '' : pin.slice(0, -1));
        }}
      />

      <Button
        variant="ghost"
        onClick={() => {
          void navigate('/sign-in');
        }}
      >
        Use password instead
      </Button>

      <style>{`@keyframes shake { 0%,100%{transform:none} 20%{transform:translateX(-7px)} 40%{transform:translateX(7px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }`}</style>
    </div>
  );
}

function messageFor(error: unknown): string {
  if (!isApiError(error)) return 'The PIN could not be checked. Try again.';

  if (error.code === ErrorCode.InvalidPin) {
    const left = error.problem?.attemptsRemaining;
    // Warned from the third attempt, not the fifth. A wipe that arrives without notice looks
    // like the app broke.
    return left !== undefined && left <= 3
      ? `Wrong PIN. ${left} ${left === 1 ? 'try' : 'tries'} left before this device is signed out.`
      : 'Wrong PIN. Try again.';
  }

  if (error.code === ErrorCode.PinNotSet) {
    return 'No PIN is set on this account. Sign in with your password, then set one from your profile.';
  }

  if (error.code === ErrorCode.Offline) {
    return 'No connection. Unlocking needs the network — the PIN is checked by the server, not this device.';
  }

  return error.message;
}
